import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { db } from "../services/firebase";
import { doc, getDoc ,setDoc } from "firebase/firestore";
import { saveData, loadData, removeData } from "../utils/storage";

const BuyerContext = createContext();
export const useBuyer = () => useContext(BuyerContext);

const STORAGE_KEY = "buyerData";
const REFRESH_INTERVAL = 6 * 60 * 60 * 1000; // 6 hours

export const BuyerProvider = ({ children }) => {
  const [buyer, setBuyer] = useState({
    buyerId: null,
    mobile: null,
    registrationStatus: null,
    operatorsBySegment: {},
    sellersBySegment: {},
    organizationName: null,
    shippingLatitude: null,
    shippingLongitude: null,
    logoUrl: null,
    retailCustomerMode: false,
    privatePasskey: [],
    cardDesign: null,
    posterThemes: {}, // { segmentId: [{ id, name, layoutType, activeTheme, activeFontPair, cardBgColor, cardTextColor, accentColor, metalValues }] }
    streetAddress: null,
    transactionalMobile: null,
    state: null,
    district: null,
  });
  const [loading, setLoading] = useState(true);

  const fetchBuyerSegmentData = async (buyerId) => {
    try {
      const buyerSegmentRef = doc(db, "BuyerSegmentRegistrations", buyerId);
      const buyerSegmentSnap = await getDoc(buyerSegmentRef);

      if (!buyerSegmentSnap.exists()) {
        return { operatorsBySegment: {}, sellersBySegment: {} };
      }

      const buyerSegmentData = buyerSegmentSnap.data();
      const operatorsBySegment = buyerSegmentData.segments || {}; // { segment: operatorId }

      // Fetch seller IDs for each operator
      const sellersBySegment = {};
      for (const [segment, operatorId] of Object.entries(operatorsBySegment)) {
        try {
          const operatorSegmentRef = doc(db, "OperatorSegmentRegistrations", operatorId);
          const operatorSegmentSnap = await getDoc(operatorSegmentRef);
          sellersBySegment[segment] = operatorSegmentSnap.exists()
            ? operatorSegmentSnap.data().sellerIds || []
            : [];
        } catch (err) {
          console.warn(`[BuyerContext] Failed to fetch sellers for operator ${operatorId}`, err);
          sellersBySegment[segment] = [];
        }
      }

      return { operatorsBySegment, sellersBySegment };
    } catch (error) {
      console.error("[BuyerContext] fetchBuyerSegmentData error:", error);
      return { operatorsBySegment: {}, sellersBySegment: {} };
    }
  };

  useEffect(() => {
    const initializeBuyer = async () => {
      try {
        // 🔄 Migrate from localStorage (if exists)
        const localStorageData = localStorage.getItem(STORAGE_KEY);
        if (localStorageData) {
          try {
            const parsed = JSON.parse(localStorageData);
            await saveData(STORAGE_KEY, parsed);
            localStorage.removeItem(STORAGE_KEY);
          } catch (e) {
            console.warn("[BuyerContext] Failed to migrate localStorage data", e);
          }
        }

        // 📥 Load from persistent storage
        const saved = await loadData(STORAGE_KEY);
        let baseBuyer = {
          buyerId: null,
          mobile: null,
          registrationStatus: null,
          operatorsBySegment: {},
          sellersBySegment: {},
          organizationName: null,
          shippingLatitude: null,
          shippingLongitude: null,
          logoUrl: null,
          retailCustomerMode: false,
          privatePasskey: [],
          streetAddress: null,
          transactionalMobile: null,
          state: null,
          district: null,
        };

if (saved) {
  baseBuyer = {
    buyerId: saved.buyerId || null,
    mobile: saved.mobile || null,
    registrationStatus: saved.registrationStatus || null,
    operatorsBySegment: saved.operatorsBySegment || {},
    sellersBySegment: saved.sellersBySegment || {},
    organizationName: saved.organizationName || null,
    shippingLatitude: saved.shippingLatitude || null,
    shippingLongitude: saved.shippingLongitude || null,
    logoUrl: saved.logoUrl || null,
    retailCustomerMode: saved.retailCustomerMode || false,
    privatePasskey: saved.privatePasskey || [],
    cardDesign: saved.cardDesign || null,
    posterDesigns: saved.posterDesigns || {},
    streetAddress: saved.streetAddress || null,
    transactionalMobile: saved.transactionalMobile || null,
    state: saved.state || null,
    district: saved.district || null,
  };
}

        // 🔄 Always fetch fresh sellersBySegment on app start / refresh / reroute
        if (baseBuyer.buyerId) {
          try {
            const freshData = await fetchBuyerSegmentData(baseBuyer.buyerId);
            baseBuyer = {
              ...baseBuyer,
              operatorsBySegment: freshData.operatorsBySegment,
              sellersBySegment: freshData.sellersBySegment,
            };
          } catch (err) {
            console.error("[BuyerContext] Failed to fetch fresh segment data on init:", err);
          }
        }

        setBuyer(baseBuyer);
      } catch (err) {
        console.error("[BuyerContext] Failed to initialize buyer data:", err);
      } finally {
        setLoading(false);
      }
    };

    initializeBuyer();
  }, []);

  // 💾 Auto-save to storage
  useEffect(() => {
    if (buyer?.buyerId) {
      const updatedData = { ...buyer, lastUpdated: Date.now() };
      saveData(STORAGE_KEY, updatedData);
    } else {
      removeData(STORAGE_KEY);
    }
  }, [buyer]);

  // 🔄 Auto-refresh segment data every 6 hours
  useEffect(() => {
    const refreshData = async () => {
      try {
        const saved = await loadData(STORAGE_KEY);
        if (!saved?.buyerId) return;

        const lastUpdated = saved.lastUpdated || 0;
        const now = Date.now();

        if (now - lastUpdated >= REFRESH_INTERVAL) {
          const freshData = await fetchBuyerSegmentData(saved.buyerId);
          setBuyer((prev) => ({
            ...prev,
            operatorsBySegment: freshData.operatorsBySegment,
            sellersBySegment: freshData.sellersBySegment,
            lastUpdated: Date.now(),
          }));
        }
      } catch (err) {
        console.error("[BuyerContext] Auto-refresh failed:", err);
      }
    };

    refreshData();
    const interval = setInterval(refreshData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const updateBuyer = useCallback((newData) => {
    setBuyer((prev) => ({ ...prev, ...newData }));
  }, []);

  const toggleRetailCustomerMode = useCallback((enabled, passkey = null) => {
    setBuyer((prev) => ({
      ...prev,
      retailCustomerMode: enabled,
    }));
  }, []);

  const refreshSegmentData = useCallback(async () => {
    try {
      const saved = await loadData(STORAGE_KEY);
      if (!saved?.buyerId) return;

      const freshData = await fetchBuyerSegmentData(saved.buyerId);
      setBuyer((prev) => ({
        ...prev,
        operatorsBySegment: freshData.operatorsBySegment,
        sellersBySegment: freshData.sellersBySegment,
        lastUpdated: Date.now(),
      }));
    } catch (err) {
      console.error("[BuyerContext] refreshSegmentData failed:", err);
    }
  }, []);

  const clearBuyer = useCallback(async () => {
    setBuyer({
      buyerId: null,
      mobile: null,
      registrationStatus: null,
      operatorsBySegment: {},
      sellersBySegment: {},
      organizationName: null,
      shippingLatitude: null,
      shippingLongitude: null,
      logoUrl: null,
      retailCustomerMode: false,
      privatePasskey: [],
      cardDesign: null,
      streetAddress: null,
      transactionalMobile: null,
      state: null,
      district: null,
    });
    await removeData(STORAGE_KEY);
  }, []);

  const saveRetailData = useCallback(async (buyerId, cardDesign) => {
    try {
      await setDoc(doc(db, "retaildata", buyerId), { cardDesign });
    } catch (error) {
      console.error("[BuyerContext] Error saving retail data:", error);
    }
  }, []);

  // Save per-segment poster design: buyer.posterDesigns[segment] = design
  const savePosterDesign = useCallback((segment, design) => {
    setBuyer((prev) => ({
      ...prev,
      posterDesigns: {
        ...(prev.posterDesigns || {}),
        [segment]: { ...design, savedAt: Date.now() },
      },
    }));
  }, []);

  const contextValue = useMemo(() => ({
    buyer,
    updateBuyer,
    clearBuyer,
    refreshSegmentData,
    toggleRetailCustomerMode,
    loading,
    saveRetailData,
    savePosterDesign,
  }), [buyer, updateBuyer, clearBuyer, refreshSegmentData, toggleRetailCustomerMode, loading, saveRetailData, savePosterDesign]);

  return (
    <BuyerContext.Provider value={contextValue}>
      {children}
    </BuyerContext.Provider>
  );
};

export default BuyerContext;
